/** Sleeping spaces per configuration, not the number of mattresses.
 * Order follows BED_OPTIONS: Twin/Twin, XL/XL, Twin/Full, XL/Queen,
 * Queen/Queen, Full/Full. Double mattresses accommodate two sleepers. */
const capacities = [2, 2, 3, 3, 4, 4] as const;
/** Badge all equally suitable sizes; prefer fewer bunks, then fewer spare spaces. */
export function recommendedConfigurations(sleepers:number):{choice:number;quantity:number}[]{
  if(!Number.isFinite(sleepers)||sleepers<1)return [];
  const options=capacities.map((capacity,choice)=>({choice,quantity:Math.ceil(sleepers/capacity),capacity}));
  const quantity=Math.min(...options.map(option=>option.quantity));
  const total=Math.min(...options.filter(option=>option.quantity===quantity).map(option=>option.quantity*option.capacity));
  return options.filter(option=>option.quantity===quantity&&option.quantity*option.capacity===total).map(({choice,quantity})=>({choice,quantity}));
}
export const bunkSleepCapacity = (choices: number[]) => choices.reduce((sum, choice) => sum + (capacities[choice] ?? 0), 0);
// Upper/lower mattress widths and lengths. Upgrades never shorten either bed.
const dimensions = [[38,75,38,75],[38,80,38,80],[38,75,54,75],[38,80,60,80],[60,80,60,80],[54,75,54,75]];
export type BunkRecommendation = {kind:'larger'|'additional';choices:number[];capacity:number};
export function bunkRecommendations(current:number[], sleepers:number):BunkRecommendation[]{
  if(!current.length || current.some(choice=>!dimensions[choice]) || bunkSleepCapacity(current)>=sleepers) return [];
  const recommendations:BunkRecommendation[]=[];
  let best:number[]|undefined;
  let bestScore=Infinity;
  function explore(next:number[]){
    if(next.length===current.length){
      const capacity=bunkSleepCapacity(next);
      if(capacity<sleepers)return;
      const changed=next.filter((choice,i)=>choice!==current[i]).length;
      const area=next.reduce((sum,choice)=>{const d=dimensions[choice];return sum+d[0]*d[1]+d[2]*d[3]},0);
      const score=changed*1e7+(capacity-sleepers)*1e6+area;
      if(score<bestScore){bestScore=score;best=next}
      return;
    }
    const original=current[next.length];
    dimensions.forEach((dims,choice)=>{
      if(dims.every((value,i)=>value>=dimensions[original][i]))explore([...next,choice]);
    });
  }
  explore([]);
  if(best)recommendations.push({kind:'larger',choices:best,capacity:bunkSleepCapacity(best)});
  const additional=[...current];
  let repeat=0;
  while(bunkSleepCapacity(additional)<sleepers && additional.length<4)additional.push(current[repeat++%current.length]);
  if(bunkSleepCapacity(additional)>=sleepers)recommendations.push({kind:'additional',choices:additional,capacity:bunkSleepCapacity(additional)});
  return recommendations;
}
/** Actual selections never depend on sleeper targets. Preserve confirmed quantities. */
export function selectedBunkChoices(preferred: number[], existing: number[] = []): number[] {
  const selected = [...new Set(preferred.filter(choice => Number.isInteger(choice) && capacities[choice] !== undefined))];
  return selected.flatMap(choice => {
    const retained = existing.filter(value => value === choice);
    return retained.length ? retained : [choice];
  });
}
/** Advisory only: callers must ask the customer before applying this suggestion. */
export function plannedBunkChoices(preferred: number[], sleepers: number): number[] {
  const selected = [...new Set(preferred.filter(choice => Number.isInteger(choice) && capacities[choice] !== undefined))];
  if (!selected.length) return [];
  const result = [...selected];
  let capacity = result.reduce((sum, choice) => sum + capacities[choice], 0);
  let repeat = 0;
  while (capacity < sleepers && result.length < 4) {
    const choice = selected[repeat++ % selected.length];
    result.push(choice);
    capacity += capacities[choice];
  }
  return result;
}


